"use client";

import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import styles from "./MMVPriceBreakup.module.scss";
import Button from "@components/atoms/Button";
import getIndianCurrencyFormat, {
  getIndianCurrencyFormatForMaskedPrice,
} from "@helpers/getIndianCurrencyFormat";
import { getLoginUrl } from "@utils/getLoginUrl";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { postLead } from "@services/leadCaptureService";
import { addUTMParamsToLeadData } from "@utils/utmParams";
import { formatCurrencyINR } from "@utils/numberFormatter";
import { getPlatform } from "@utils/platformDetection.client";
import Text from "@components/atoms/Text";

interface PriceBreakdownProps {
  exShowroomPrice: string;
  otherCharges: any;
  otherChargesTotal: string;
  onRoadPrice: number;
  ackoDiscount: string;
  ackoDrivePrice: string;
  deliveryDate: string;
  cityName: string;
  isLoggedIn: boolean;
  setIsModalOpen: (val: boolean) => void;
  setIsIntentModalOpen: (val: boolean) => void;
  isInventoryAvailable: boolean;
  deliveryType: string;
  isSellable: boolean;
  pageType?: string;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  carAvailability?: string;
  savingsBreakup?: any;
  selectedColor?: any;
  savingBalance?: any;
  environmentCompensation?: number;
  fuelType?: string;
}

export default function PriceBreakup({
  exShowroomPrice,
  otherCharges,
  otherChargesTotal,
  onRoadPrice,
  ackoDiscount,
  ackoDrivePrice,
  deliveryDate,
  cityName,
  isLoggedIn,
  isInventoryAvailable,
  deliveryType,
  setIsModalOpen,
  setIsIntentModalOpen,
  isSellable,
  pageType,
  brandName,
  modelName,
  variantName,
  carAvailability,
  savingsBreakup,
  selectedColor,
  savingBalance,
  environmentCompensation,
  fuelType
}: PriceBreakdownProps) {
  const [showOtherCharges, setShowOtherCharges] = useState(false);
  const { consumerdiscount: consumerDiscount } = savingsBreakup || {};
  const getRIPPlusConsumer = (savingsBreakup: { ripdiscount: any; consumerdiscount: any; }) => {
    return (
      savingsBreakup?.ripdiscount + savingsBreakup?.consumerdiscount || 0
    );
  };
  // const [selectedLanguage, setSelectedLanguage] = useState("KA");
  const toggleOtherCharges = async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_price_breakup",
        car_availability: carAvailability,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_variant: variantName,
        vehicle_color: selectedColor?.brand_color || "",
        city_name: cityName,
      },
    });
    setShowOtherCharges(!showOtherCharges);
    if (!showOtherCharges && isLoggedIn) {
      let leadData: any = {
        city: cityName,
        make: brandName || "",
        model: modelName || "",
        variant: variantName || "",
        color: selectedColor?.brand_color || "",
      };
      // const storedSource = sessionStorage.getItem("referrerSource");
      const utmTerm =
        new URL(window.location.href).searchParams.get("utm_term") || "";
      leadData.dump_data = {
        mx_Platform: getPlatform(),
        mx_Page_Type_Web: pageType,
        mx_Login_Type: "Viewed detailed price",
        // SourceReferrerURL: storedSource,
        mx_URL: window.location.href || process.env.NEXT_PUBLIC_FRONTEND_HOST,
        mx_UTM_Term: utmTerm,
        mx_color: leadData?.color || "",
      };
      let additionalLeaddata = JSON.parse(
        sessionStorage.getItem("leadData") || "{}"
      );

      if (additionalLeaddata) {
        additionalLeaddata = { ...additionalLeaddata, ...leadData };
        leadData = { ...additionalLeaddata, ...leadData };
      } else {
        additionalLeaddata = { ...leadData };
      }
      const buyingPeriod = leadData?.buying_period || "";
      try {
        const leadResponse = await postLead(addUTMParamsToLeadData(leadData));
        if (!leadResponse.success) {
          console.error("Lead capture failed.", leadResponse);
          await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
            pageType: pageType,
            additionalProps: {
              exception_type: "API issue",
              exception_name: "failed lead creation",
              car_availability: carAvailability,
              vehicle_make: brandName,
              vehicle_model: modelName,
              vehicle_variant: variantName,
              city_name: cityName,
              login_type: "Viewed detailed price",
              vehicle_color: selectedColor?.brand_color || "",
              intent: buyingPeriod,
            },
          });
        } else {
          const leadCreated = !!leadResponse?.data?.is_created;
          await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
            pageType: pageType,
            additionalProps: {
              action_type: leadCreated ? "lead_created" : "lead_updated",
              car_availability: carAvailability,
              vehicle_make: brandName,
              vehicle_model: modelName,
              vehicle_variant: variantName,
              vehicle_color: selectedColor?.brand_color || "",
              city_name: cityName,
              login_type: "Viewed detailed price",
              intent: buyingPeriod,
            },
          });
        }
      } catch (error) {
        console.error("Error in postLead API call:", error);
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType: pageType,
          additionalProps: {
            exception_type: "API issue",
            exception_name: "failed lead creation",
            car_availability: carAvailability,
            vehicle_make: brandName,
            vehicle_model: modelName,
            vehicle_variant: variantName,
            vehicle_color: selectedColor?.brand_color || "",
            city_name: cityName,
            login_type: "Viewed detailed price",
            intent: buyingPeriod,
          },
        });
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Price breakup</h2>
        {/* <div className={styles.languageSelector}>
          <button
            className={`${styles.languageButton} ${
              selectedLanguage === "KA" ? styles.active : ""
            }`}
            onClick={() => setSelectedLanguage("KA")}
          >
            KA
          </button>
          <button
            className={`${styles.languageButton} ${
              selectedLanguage === "BH" ? styles.active : ""
            }`}
            onClick={() => setSelectedLanguage("BH")}
          >
            BH
          </button>
        </div> */}
      </div>

      <div className={styles.priceCard}>
        <div className={styles.priceComponents}>
          <div className={styles.priceRow}>
            <span className={styles.priceLabel}>Ex-showroom price</span>
            <span className={styles.priceValue}>
              {`₹${getIndianCurrencyFormat(exShowroomPrice)}`}
            </span>
          </div>

          <div className={styles.priceRow} onClick={toggleOtherCharges}>
            <span className={styles.priceLabel}>
              Other charges
              <ChevronDown
                className={`${styles.chevron} ${
                  showOtherCharges ? styles.rotate : ""
                }`}
                size={18}
              />
            </span>
            <span className={styles.priceValue}>
              {`₹${getIndianCurrencyFormat(otherChargesTotal)}`}
            </span>
          </div>

          {showOtherCharges && (
            <div className={styles.otherChargesDetails}>
              {otherCharges.map((charge: any, index: number) => (
                <div key={index} className={styles.chargeRow}>
                  <span>{charge.label}</span>
                  <span>{`${formatCurrencyINR(charge.value)}`}</span>
                </div>
              ))}
            </div>
          )}
          {consumerDiscount > 0 && (
            <>
              <div className={styles.divider}></div>
              <div className={styles.priceRow}>
                <span className={styles.priceLabel}>Consumer Savings</span>
                <span className={styles.priceValue}>
                  {`₹${getIndianCurrencyFormat(consumerDiscount)}`}
                </span>
              </div>
            </>
          )}

          <div className={styles.divider}></div>
          <div className={styles.priceRow}>
            <span className={styles.priceLabel}>
              {`On-road price in ${cityName}`}
            </span>
            <span className={styles.priceValue}>
              {`₹${getIndianCurrencyFormat(
                isLoggedIn
                  ? Number(onRoadPrice || 0) -
                      getRIPPlusConsumer(savingsBreakup)
                  : onRoadPrice
              )}`}
            </span>
          </div>

          {isSellable && (
            <div className={styles.priceRow}>
              <span className={styles.priceLabel}>ACKO Drive discount</span>
              <span className={styles.discountValue}>
                {` - ₹${
                  isLoggedIn
                    ? getIndianCurrencyFormat(savingsBreakup?.ackodiscount || 0)
                    : getIndianCurrencyFormatForMaskedPrice(
                        savingsBreakup?.ackodiscount || ""
                      )
                }`}
              </span>
            </div>
          )}

          {savingBalance > 0 && (
            <Text className={styles.savingBalanceText}>
              (
              <span className={styles.savingBalanceValue}>
                {formatCurrencyINR(savingBalance)}
              </span>{" "}
              New car savings balance applied)
            </Text>
          )}
        </div>
        {isSellable && (
          <div className={styles.ackoPriceContainer}>
            <div className={styles.ackoPriceRow}>
              <span
                className={styles.ackoPriceLabel}
              >{`ACKO Drive price`}</span>
              <span className={styles.ackoPriceValue}>
                {`₹${
                  isLoggedIn
                    ? getIndianCurrencyFormat(ackoDrivePrice)
                    : getIndianCurrencyFormatForMaskedPrice(ackoDrivePrice)
                }`}
              </span>
            </div>
            {isSellable && (
              fuelType?.toLowerCase() === 'electric' ? (
                <div className={styles.gstSavingsText}>
                  No GST impact on EV cars, prices are the same before and after.
                </div>
              ) : (
                environmentCompensation && environmentCompensation > 1 && (
                  <div className={styles.gstSavingsText}>
                    Good news! This price already includes your GST savings of ₹
                    {getIndianCurrencyFormat(environmentCompensation)}
                  </div>
                )
              )
            )}

            <div className={styles.infoBox}>
              <p className={styles.infoText}>
                <span className={styles.infoLabel}>{`Did you know? `}</span>
                {`Our prices are lower than dealership price because we don't charge any commissions`}
              </p>
            </div>
          </div>
        )}
      </div>
      {isSellable && (
        <p className={styles.disclaimer}>
          Prices are subject to change based on your delivery date
        </p>
      )}
    </div>
  );
}
